import { Alert, AlertDescription, AlertTitle } from '@noor/ui';
import {
  actorName,
  formatDateTime,
  isRequestAmended,
  isSuperseded,
  isSystemSettled,
  requestReviewerName,
  type ChangeRequest,
  type ModerationDecision,
  type ModerationOperation,
  type Translate,
} from '@/entities/moderation';
import { InfoRow } from './review-modal-shell';

interface VerdictBannerProps {
  request: ChangeRequest;
  decision: Exclude<ModerationDecision, 'pending'>;
  operation: ModerationOperation;
  t: Translate;
  inline?: boolean;
}

const VARIANT = {
  approved: 'success',
  rejected: 'destructive',
  closed: 'neutral',
} as const;

/**
 * Read-only outcome of a settled request, pinned above the content: who
 * decided and when, the rejection reason, a reviewer's amendment, and why a
 * request closed without a verdict (stale / withdrawn / superseded). Never
 * invents a reason — a rejection without one says so.
 */
export function VerdictBanner({
  request,
  decision,
  operation,
  t,
  inline = false,
}: VerdictBannerProps) {
  const reviewedAt = request.reviewed_at?.trim() ?? '';
  const reviewer = requestReviewerName(request);
  const hasReviewer = reviewer !== '—';
  const system = isSystemSettled(request);
  const reason = request.rejection_reason?.trim() ?? '';

  const details = (
    <>
      {hasReviewer && !system && (
        <p>
          {t(`review.actor.${decision}`, ACTOR_FALLBACK[decision]).replace(
            '{{name}}',
            reviewer,
          )}
        </p>
      )}
      {system && decision !== 'closed' && (
        <p>
          {t(
            'review.systemSettled',
            'Решение принято автоматически — модерация для этой операции отключена.',
          )}
        </p>
      )}
      {reviewedAt !== '' && (
        <p className="tabular-nums">
          {t('review.decidedAt', 'Дата решения: {{date}}').replace(
            '{{date}}',
            formatDateTime(reviewedAt),
          )}
        </p>
      )}
      {decision === 'rejected' && (
        <p className="font-medium whitespace-pre-line">
          {t('review.reason', 'Причина: {{reason}}').replace(
            '{{reason}}',
            reason === '' ? t('review.noReason', 'не указана') : reason,
          )}
        </p>
      )}
      {decision === 'closed' && <p>{closedExplanation(request, t)}</p>}
      {isRequestAmended(request) && (
        <p>
          {t(
            'review.amended',
            'Перед одобрением модератор {{name}} изменил заявку ({{date}}).',
          )
            .replace('{{name}}', actorName(request.amender))
            .replace('{{date}}', formatDateTime(request.amended_at))}
        </p>
      )}
    </>
  );
  if (inline) {
    return (
      <div className="contents" data-testid="moderation-verdict">
        {hasReviewer && !system && (
          <InfoRow
            label={
              t(`review.actor.${decision}`, ACTOR_FALLBACK[decision]).split(':')[0] ?? ''
            }
            value={reviewer}
          />
        )}
        {system && (
          <InfoRow
            label={t('table.status', 'Статус')}
            value={t(
              'review.systemSettled',
              'Решение принято автоматически — модерация для этой операции отключена.',
            )}
          />
        )}
        {reviewedAt !== '' && (
          <InfoRow
            label={t('review.decidedAt', 'Дата решения: {{date}}').split(':')[0] ?? ''}
            value={formatDateTime(reviewedAt)}
            numeric
          />
        )}
        {decision === 'rejected' && (
          <InfoRow
            label={t('review.reason', 'Причина: {{reason}}').split(':')[0] ?? ''}
            value={
              <span className="whitespace-pre-line">
                {reason || t('review.noReason', 'не указана')}
              </span>
            }
          />
        )}
        {isRequestAmended(request) && (
          <InfoRow
            label={t('review.amendmentLabel', 'Изменение модератором')}
            value={t(
              'review.amended',
              'Перед одобрением модератор {{name}} изменил заявку ({{date}}).',
            )
              .replace('{{name}}', actorName(request.amender))
              .replace('{{date}}', formatDateTime(request.amended_at))}
          />
        )}
      </div>
    );
  }
  return (
    <Alert variant={VARIANT[decision]} data-testid="moderation-verdict">
      <AlertTitle>{verdictTitle(request, decision, operation, t)}</AlertTitle>
      <AlertDescription>{details}</AlertDescription>
    </Alert>
  );
}

function verdictTitle(
  request: ChangeRequest,
  decision: VerdictBannerProps['decision'],
  operation: ModerationOperation,
  t: Translate,
): string {
  if (decision === 'approved') {
    return t(
      `review.verdict.approved.${operation}`,
      VERDICT_FALLBACK.approved[operation],
    );
  }
  if (decision === 'rejected') {
    return t(
      `review.verdict.rejected.${operation}`,
      VERDICT_FALLBACK.rejected[operation],
    );
  }
  const status = (request.status ?? '').trim().toLowerCase();
  if (status === 'stale') return t('review.verdict.stale', 'Заявка устарела');
  if (status === 'withdrawn') return t('review.verdict.withdrawn', 'Заявка отозвана');
  return t('review.verdict.closed', 'Заявка закрыта');
}

const ACTOR_FALLBACK = {
  approved: 'Одобрил: {{name}}',
  rejected: 'Отклонил: {{name}}',
  closed: 'Закрыл: {{name}}',
} as const;

const VERDICT_FALLBACK = {
  approved: {
    create: 'Публикация одобрена',
    update: 'Изменения одобрены',
    delete: 'Удаление одобрено',
  },
  rejected: {
    create: 'Публикация отклонена',
    update: 'Изменения отклонены',
    delete: 'Удаление отклонено',
  },
} as const;

function closedExplanation(request: ChangeRequest, t: Translate): string {
  const status = (request.status ?? '').trim().toLowerCase();
  if (isSuperseded(request)) {
    return t(
      'review.supersededHint',
      'Мерчант отправил более новую версию — она заменила эту заявку.',
    );
  }
  if (status === 'withdrawn') {
    return t('review.withdrawnHint', 'Заявку отозвал мерчант до решения модератора.');
  }
  if (status === 'stale') {
    return t(
      'review.staleHint',
      'Объект изменился после подачи заявки, и она больше не применима.',
    );
  }
  return t('review.closedHint', 'Заявка закрыта без решения модератора.');
}
